// ═══════════════════════════════════════════════════════════════════
//  WEDOO INTERIORS — STANDALONE QUOTATION MODULE
//  /admin/quotation  (All / Create / Draft / Approved / Rejected)
//  Independent of the CRM lead-bound quotation builder.
// ═══════════════════════════════════════════════════════════════════
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { sanitizePhone, isValidOptionalPhone, PHONE_ERROR, phoneInputProps } from '../utils/phone';
import SearchableSelect from '../components/SearchableSelect';
import { DOC_THEMES, DEFAULT_THEME, resolveTheme, loadThemeChoice, saveThemeChoice } from '../print/docThemes';

const API_BASE = process.env.REACT_APP_API_URL?.replace(/\/api$/, '') || '';
const imgSrc = (p) => !p ? '' : /^https?:\/\//i.test(p) ? p : `${API_BASE}${p}`;

// ── Constants ─────────────────────────────────────────────────────
export const PROJECT_TYPES = [
  'Residential Interior', 'Villa Interior', 'Apartment Interior', 'Modular Kitchen',
  'Bedroom Interior', 'Living Room Interior', 'Office Interior', 'Commercial Interior',
  'Restaurant / Cafe Interior', 'Retail Shop Interior', 'Renovation Work', 'Custom Interior',
];

const STATUS_LIST = ['Draft', 'Sent', 'Under Discussion', 'Approved', 'Rejected', 'Expired', 'Converted to Project'];

const STATUS_COLORS = {
  'Draft':                 { bg: 'rgba(148,163,184,0.1)', color: '#94a3b8', border: 'rgba(148,163,184,0.25)' },
  'Sent':                  { bg: 'rgba(96,165,250,0.1)',  color: '#60a5fa', border: 'rgba(96,165,250,0.25)' },
  'Under Discussion':      { bg: 'rgba(167,139,250,0.1)', color: '#a78bfa', border: 'rgba(167,139,250,0.25)' },
  'Approved':              { bg: 'rgba(74,222,128,0.1)',  color: '#4ade80', border: 'rgba(74,222,128,0.25)' },
  'Rejected':              { bg: 'rgba(248,113,113,0.1)', color: '#f87171', border: 'rgba(248,113,113,0.25)' },
  'Expired':               { bg: 'rgba(251,146,60,0.1)',  color: '#fb923c', border: 'rgba(251,146,60,0.25)' },
  'Converted to Project':  { bg: 'rgba(245,168,0,0.12)',  color: '#F5A800', border: 'rgba(245,168,0,0.3)' },
};

export const CATEGORY_PRESETS = {
  'Civil Works':      ['False Ceiling', 'Wall Partition', 'Flooring', 'Painting', 'Electrical Work', 'Plumbing Work'],
  'Modular Kitchen':  ['Base Cabinet', 'Wall Cabinet', 'Tall Unit', 'Granite Countertop', 'Kitchen Accessories', 'Sink', 'Chimney Installation'],
  'Living Room':      ['TV Unit', 'Sofa Back Panel', 'False Ceiling', 'Display Unit', 'Wall Panelling'],
  'Bedroom':          ['Wardrobe', 'Cot', 'Bedside Table', 'Dressing Unit', 'Study Table', 'Loft Storage'],
  'Bathroom':         ['Vanity Unit', 'Mirror', 'Shower Partition', 'Accessories'],
  'Office Interior':  ['Workstations', 'Manager Cabin', 'Meeting Table', 'Reception Counter', 'Storage Units', 'Glass Partition', 'False Ceiling'],
};

const UNITS = ['Sq.ft', 'Rft', 'Nos', 'Set', 'Lumpsum'];

// Dimensions are millimetres. The trade sizes work at 300 mm = 1 ft and bills
// by area = length x height; depth is part of the spec but not the area.
// Keep in step with areaSft/lineAmount in backend/models/index.js.
const MM_PER_FT = 300;
const rawAreaSft = (length, height) => {
  const l = parseFloat(length) || 0;
  const h = parseFloat(height) || 0;
  if (l <= 0 || h <= 0) return 0;
  return (l / MM_PER_FT) * (h / MM_PER_FT);
};
// Shown to 2 dp, but billed from the unrounded area so 8.33 sft x 1800 reads as
// exactly 15,000 rather than 14,994.
const areaSft = (length, height) => Math.round(rawAreaSft(length, height) * 100) / 100;
// Rows with dimensions bill as sft x qty x rate; rows without them (older
// quotations, and Nos/Set/Lumpsum lines) keep the original qty x rate.
const lineAmount = (i = {}) => {
  const qty = parseFloat(i.quantity || 0);
  const rate = parseFloat(i.rate || 0);
  const area = rawAreaSft(i.length, i.height);
  return area > 0 ? area * qty * rate : qty * rate;
};

const emptyItem = () => ({ description: '', length: '', depth: '', height: '', material: '', unit: 'Sq.ft', quantity: 1, rate: 0, amount: 0 });
const emptyCategory = (name = '') => ({ categoryName: name, items: [emptyItem()] });

const inr = (n) => `₹${Math.round(n || 0).toLocaleString('en-IN')}`;
// PDF-only: standard PDF fonts (Helvetica) have no glyph for ₹, so use "Rs." there instead of inr().
const inrPdf = (n) => `Rs. ${Math.round(n || 0).toLocaleString('en-IN')}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const fmtDateLong = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' }) : '—';
const toInputDate = (d) => d ? new Date(d).toISOString().split('T')[0] : '';

// ── Calculation (mirrors backend pre-save hook) ─────────────────────
function computeTotals(form) {
  const items = (form.categories || []).flatMap(c => c.items || []);
  const subtotal = items.reduce((s, i) => s + lineAmount(i), 0);
  const discountAmount = form.discountType === 'percent'
    ? subtotal * (parseFloat(form.discountValue) || 0) / 100
    : (parseFloat(form.discountValue) || 0);
  const taxableAmount = subtotal - discountAmount;
  const gstAmount = taxableAmount * (parseFloat(form.gstPercent ?? 18) || 0) / 100;
  const transportationCharges = parseFloat(form.transportationCharges) || 0;
  const installationCharges = parseFloat(form.installationCharges) || 0;
  const additionalCharges = parseFloat(form.additionalCharges) || 0;
  const grandTotal = taxableAmount + gstAmount + transportationCharges + installationCharges + additionalCharges;
  const advanceAmount = form.advancePaymentType === 'percent'
    ? grandTotal * (parseFloat(form.advancePaymentValue) || 0) / 100
    : (parseFloat(form.advancePaymentValue) || 0);
  return { subtotal, discountAmount, taxableAmount, gstAmount, transportationCharges, installationCharges, additionalCharges, grandTotal, advanceAmount };
}

// ── UI bits ──────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const s = STATUS_COLORS[status] || STATUS_COLORS.Draft;
  return <span style={{ padding: '0.22rem 0.65rem', fontSize: '0.62rem', fontWeight: 500, letterSpacing: '0.06em', background: s.bg, color: s.color, border: `1px solid ${s.border}`, whiteSpace: 'nowrap' }}>{status}</span>;
};

const FieldGroup = ({ label, children }) => (
  <div className="form-group" style={{ marginBottom: '1rem' }}>
    {label && <label>{label}</label>}
    {children}
  </div>
);

// ── PDF generation (jsPDF + autoTable) ───────────────────────────────
function loadImageDataUrl(url) {
  return new Promise((resolve) => {
    if (!url) return resolve(null);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth; canvas.height = img.naturalHeight;
        canvas.getContext('2d').drawImage(img, 0, 0);
        resolve({ dataUrl: canvas.toDataURL('image/png'), width: img.naturalWidth, height: img.naturalHeight });
      } catch { resolve(null); }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

export async function buildQuotationPDF(quotation, themeKey = DEFAULT_THEME) {
  const th = resolveTheme(themeKey);
  const settings = (await api.get('/settings').catch(() => ({ data: {} }))).data || {};
  const [logo, signature] = await Promise.all([
    loadImageDataUrl(settings.logo ? imgSrc(settings.logo) : null),
    loadImageDataUrl(settings.signature ? imgSrc(settings.signature) : null),
  ]);

  const t = computeTotals(quotation);
  const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const gold = th.accent;
  let y = 34;

  const footer = () => {
    const pages = doc.internal.getNumberOfPages();
    for (let p = 1; p <= pages; p++) {
      doc.setPage(p);
      doc.setFontSize(7.5); doc.setTextColor(150, 150, 150); doc.setFont(undefined, 'normal');
      doc.text(`Page ${p} of ${pages}`, pageWidth - 30, pageHeight - 20, { align: 'right' });
    }
  };
  const ensureSpace = (need) => {
    if (y + need > pageHeight - 45) { doc.addPage(); y = 34; }
  };

  // Header — one of seven treatments, chosen by the theme.
  const companyName = settings.companyName || 'Wedoo Interiors';
  // Parent group line printed under the logo. Reads from Settings when that
  // field is filled in, so it can be changed without touching this file.
  const groupName = settings.groupName || 'Wedoo Groups';

  // The logo carries the company name already, so it is used in place of the
  // name rather than beside it, with the group line beneath. Falls back to type
  // when no logo is configured in Settings. Returns the total height consumed
  // (logo + group line) so callers can position whatever follows.
  const drawBrand = ({ x, y: top, width = 120, align = 'left', size = 14, color = th.ink }) => {
    let used;
    if (logo) {
      const w = width;
      const h = w * ((logo.height || 158) / (logo.width || 650));
      const lx = align === 'center' ? x - w / 2 : align === 'right' ? x - w : x;
      doc.addImage(logo.dataUrl, 'PNG', lx, top, w, h);
      used = h;
    } else {
      doc.setTextColor(color[0], color[1], color[2]);
      doc.setFont(undefined, 'bold'); doc.setFontSize(size);
      doc.text(companyName, x, top + size * 0.85, { align });
      used = size * 1.15;
    }
    if (groupName) {
      doc.setTextColor(color[0], color[1], color[2]);
      doc.setFont(undefined, 'bold'); doc.setFontSize(9.5);
      doc.text(groupName, x, top + used + 11, { align });
      used += 15;
    }
    return used;
  };

  if (th.header === 'band') {
    // Full-width accent band carrying the brand and the document title.
    const bandH = 80;
    doc.setFillColor(th.tableHead[0], th.tableHead[1], th.tableHead[2]);
    doc.rect(0, 0, pageWidth, bandH, 'F');
    drawBrand({ x: 40, y: 18, width: 120, size: 15, color: th.tableHeadInk });
    doc.setTextColor(th.tableHeadInk[0], th.tableHeadInk[1], th.tableHeadInk[2]);
    doc.setFont(undefined, 'bold'); doc.setFontSize(13);
    doc.text('QUOTATION', pageWidth - 40, 46, { align: 'right' });
    y = bandH + 22;
  } else if (th.header === 'split') {
    // Accent block carrying the document title, brand alongside it.
    const bandH = 80;
    doc.setFillColor(th.tableHead[0], th.tableHead[1], th.tableHead[2]);
    doc.rect(0, 0, 200, bandH, 'F');
    doc.setTextColor(th.tableHeadInk[0], th.tableHeadInk[1], th.tableHeadInk[2]);
    doc.setFont(undefined, 'bold'); doc.setFontSize(16);
    doc.text('QUOTATION', 40, 47);
    drawBrand({ x: 224, y: 18, width: 118, size: 13 });
    y = bandH + 22;
  } else if (th.header === 'stripe') {
    // Thin accent stripe across the very top, then a centred brand.
    doc.setFillColor(gold[0], gold[1], gold[2]);
    doc.rect(0, 0, pageWidth, 7, 'F');
    y = 34;
    y += drawBrand({ x: pageWidth / 2, y, width: 130, align: 'center', size: 14 }) + 24;
  } else if (th.header === 'rail') {
    // Vertical accent rail down the left edge of the first page.
    doc.setFillColor(gold[0], gold[1], gold[2]);
    doc.rect(0, 0, 14, 160, 'F');
    const brandH = drawBrand({ x: 40, y, width: 120, size: 15 });
    doc.setFont(undefined, 'bold'); doc.setFontSize(11);
    doc.setTextColor(th.ink[0], th.ink[1], th.ink[2]);
    doc.text('QUOTATION', pageWidth - 40, y + 14, { align: 'right' });
    y += brandH + 24;
  } else if (th.header === 'boxed') {
    // Brand and title inside an outlined box — no fills, minimal ink.
    const boxTop = y;
    const brandH = drawBrand({ x: 54, y: boxTop + 14, width: 116, size: 14 });
    const boxH = Math.max(58, brandH + 28);
    doc.setDrawColor(gold[0], gold[1], gold[2]); doc.setLineWidth(1);
    doc.rect(40, boxTop, pageWidth - 80, boxH);
    doc.setDrawColor(gold[0], gold[1], gold[2]); doc.setLineWidth(0.7);
    doc.line(pageWidth - 150, boxTop + 22, pageWidth - 54, boxTop + 22);
    doc.setFont(undefined, 'bold'); doc.setFontSize(12);
    doc.setTextColor(gold[0], gold[1], gold[2]);
    doc.text('QUOTATION', pageWidth - 54, boxTop + 40, { align: 'right' });
    y = boxTop + boxH + 22;
  } else if (th.header === 'rule') {
    // Brand and rules only — nothing that needs colour ink.
    const brandH = drawBrand({ x: 40, y, width: 112, size: 14 });
    doc.setFont(undefined, 'normal'); doc.setFontSize(10);
    doc.setTextColor(th.ink[0], th.ink[1], th.ink[2]);
    doc.text('QUOTATION', pageWidth - 40, y + 18, { align: 'right' });
    y += brandH + 10;
    doc.setDrawColor(th.ink[0], th.ink[1], th.ink[2]); doc.setLineWidth(1.2);
    doc.line(40, y, pageWidth - 40, y);
    y += 20;
  } else {
    // Centred masthead (Classic).
    y += drawBrand({ x: pageWidth / 2, y, width: 130, align: 'center', size: 14 }) + 12;
    doc.setDrawColor(gold[0], gold[1], gold[2]); doc.setLineWidth(1);
    doc.line(pageWidth / 2 - 90, y, pageWidth / 2 + 90, y);
    y += 16;
  }

  // Quotation meta
  doc.setFont(undefined, 'normal'); doc.setFontSize(9.5); doc.setTextColor(40, 40, 40);
  doc.text(`Quotation No: ${quotation.quotationNo || '(will be generated)'}`, 40, y);
  doc.text(`Date: ${fmtDateLong(quotation.quotationDate)}`, pageWidth - 40, y, { align: 'right' });
  y += 14;
  doc.text(`Valid Until: ${fmtDateLong(quotation.validUntil)}`, 40, y);
  if (quotation.salesPerson) doc.text(`Prepared By: ${quotation.salesPerson}`, pageWidth - 40, y, { align: 'right' });
  y += 16;

  // Client details
  doc.setFillColor(th.panel[0], th.panel[1], th.panel[2]);
  doc.rect(40, y - 12, pageWidth - 80, 78, 'F');
  doc.setFont(undefined, 'bold'); doc.setFontSize(9.5);
  doc.text('Client Details', 50, y);
  y += 14;
  doc.setFont(undefined, 'normal'); doc.setFontSize(9);
  doc.text(`Client Name: ${quotation.clientName || '-'}`, 50, y);
  doc.text(`Mobile: ${quotation.clientMobile || '-'}`, pageWidth / 2 + 10, y);
  y += 13;
  doc.text(`Email: ${quotation.clientEmail || '-'}`, 50, y);
  doc.text(`Project Type: ${quotation.projectType || '-'}`, pageWidth / 2 + 10, y);
  y += 13;
  const addrLines = doc.splitTextToSize(`Project Address: ${quotation.projectAddress || '-'}`, pageWidth - 100);
  doc.text(addrLines, 50, y);
  y += addrLines.length * 11 + 14;

  // Project Scope & Cost Estimate
  doc.setFont(undefined, 'bold'); doc.setFontSize(10.5); doc.setTextColor(20, 20, 20);
  doc.text('Project Scope & Cost Estimate', 40, y);
  y += 8;

  const body = [];
  let sno = 1;
  (quotation.categories || []).forEach(cat => {
    if (!cat.items?.length) return;
    body.push([{ content: cat.categoryName || 'Other', colSpan: 9, styles: { fontStyle: 'bold', fillColor: th.band, textColor: th.bandInk } }]);
    cat.items.forEach(it => {
      const sft = areaSft(it.length, it.height);
      body.push([
        sno++,
        it.description || '-',
        it.length || '-', it.depth || '-', it.height || '-',
        it.material || '-',
        it.quantity || 0,
        sft > 0 ? sft.toFixed(2) : (it.unit || '-'),
        inrPdf(lineAmount(it)),
      ]);
    });
  });
  if (!body.length) body.push([{ content: 'No items added', colSpan: 9, styles: { halign: 'center', textColor: [150, 150, 150] } }]);

  autoTable(doc, {
    head: [[
      'SL.No', 'Item / Specification',
      { content: 'Length', styles: { halign: 'right' } },
      { content: 'Depth', styles: { halign: 'right' } },
      { content: 'Height', styles: { halign: 'right' } },
      'Material',
      { content: 'Qty', styles: { halign: 'right' } },
      { content: 'Unit/Sft', styles: { halign: 'right' } },
      { content: 'Amount', styles: { halign: 'right' } },
    ]],
    body, startY: y,
    styles: { fontSize: 7.2, cellPadding: 3, lineColor: [210, 210, 210], lineWidth: 0.5, overflow: 'linebreak' },
    headStyles: { fillColor: th.tableHead, textColor: th.tableHeadInk, lineWidth: 0.5, fontSize: 7 },
    ...(th.zebra ? { alternateRowStyles: { fillColor: th.zebra } } : {}),
    columnStyles: {
      0: { cellWidth: 26, halign: 'center' },
      1: { cellWidth: 118 },
      2: { cellWidth: 36, halign: 'right' },
      3: { cellWidth: 32, halign: 'right' },
      4: { cellWidth: 36, halign: 'right' },
      5: { cellWidth: 108 },
      6: { cellWidth: 26, halign: 'right' },
      7: { cellWidth: 42, halign: 'right' },
      8: { cellWidth: 66, halign: 'right' },
    },
    margin: { left: 40, right: 40 },
  });
  y = doc.lastAutoTable.finalY + 16;
  ensureSpace(140);

  // Cost Summary
  doc.setFont(undefined, 'bold'); doc.setFontSize(10.5); doc.setTextColor(20, 20, 20);
  doc.text('Cost Summary', 40, y);
  y += 8;
  const summaryRows = [['Subtotal', inrPdf(t.subtotal)]];
  if (t.discountAmount) summaryRows.push([`Discount (${quotation.discountType === 'percent' ? `${quotation.discountValue || 0}%` : 'Fixed'})`, `- ${inrPdf(t.discountAmount)}`]);
  summaryRows.push(['Taxable Amount', inrPdf(t.taxableAmount)]);
  summaryRows.push([`GST (${quotation.gstPercent ?? 18}%)`, inrPdf(t.gstAmount)]);
  if (t.transportationCharges) summaryRows.push(['Transportation Charges', inrPdf(t.transportationCharges)]);
  if (t.installationCharges) summaryRows.push(['Installation Charges', inrPdf(t.installationCharges)]);
  if (t.additionalCharges) summaryRows.push([quotation.additionalChargesNote || 'Additional Charges', inrPdf(t.additionalCharges)]);
  summaryRows.push([{ content: 'Grand Total', styles: { fontStyle: 'bold', fillColor: [255, 244, 222] } }, { content: inrPdf(t.grandTotal), styles: { fontStyle: 'bold', fillColor: [255, 244, 222], halign: 'right' } }]);

  autoTable(doc, {
    body: summaryRows, startY: y,
    styles: { fontSize: 9, lineColor: [210, 210, 210], lineWidth: 0.5 },
    columnStyles: { 0: { cellWidth: pageWidth - 80 - 120 }, 1: { cellWidth: 120, halign: 'right' } },
    margin: { left: 40, right: 40 },
    tableWidth: pageWidth - 80,
  });
  y = doc.lastAutoTable.finalY + 16;
  ensureSpace(120);

  // Payment Terms
  doc.setFont(undefined, 'bold'); doc.setFontSize(10.5); doc.setTextColor(20, 20, 20);
  doc.text('Payment Terms', 40, y);
  y += 12;
  doc.setFont(undefined, 'normal'); doc.setFontSize(9);
  (quotation.paymentTerms || '').split('\n').filter(Boolean).forEach(line => {
    const wrapped = doc.splitTextToSize(`•  ${line.trim()}`, pageWidth - 90);
    ensureSpace(wrapped.length * 11 + 3);
    doc.text(wrapped, 50, y);
    y += wrapped.length * 11 + 3;
  });
  y += 8;

  // Terms & Conditions
  ensureSpace(24);
  doc.setFont(undefined, 'bold'); doc.setFontSize(10.5);
  doc.text('Terms & Conditions', 40, y);
  y += 12;
  doc.setFont(undefined, 'normal'); doc.setFontSize(8.5);
  (quotation.termsConditions || '').split('\n').filter(Boolean).forEach((line, i) => {
    const wrapped = doc.splitTextToSize(`${i + 1}. ${line.trim()}`, pageWidth - 90);
    ensureSpace(wrapped.length * 10 + 3);
    doc.text(wrapped, 50, y);
    y += wrapped.length * 10 + 3;
  });
  y += 14;

  // Signatures
  ensureSpace(76);
  const sigY = y + 36;
  doc.setFont(undefined, 'normal'); doc.setFontSize(9);
  doc.text('Client Signature: ___________________', 50, sigY);
  if (signature) {
    const sigW = 90, sigH = sigW * ((signature.height || 40) / (signature.width || 150));
    doc.addImage(signature.dataUrl, 'PNG', pageWidth - 50 - sigW, sigY - sigH - 6, sigW, sigH);
  }
  doc.text('Authorized Signatory: ___________________', pageWidth - 50, sigY, { align: 'right' });
  y = sigY + 30;

  ensureSpace(24);
  doc.setFont(undefined, 'italic'); doc.setFontSize(9.5); doc.setTextColor(gold[0], gold[1], gold[2]);
  doc.text('Thank you for choosing our Interior Design Services.', pageWidth / 2, y, { align: 'center' });

  footer();
  return doc;
}

// ═══════════════════════════════════════════════════════════════════
//  ALL QUOTATIONS — list page
// ═══════════════════════════════════════════════════════════════════
export function AdminQuotationList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pdfBusy, setPdfBusy] = useState('');
  const [shareMenuFor, setShareMenuFor] = useState(null); // { id, top, left } of the open share dropdown, or null
  // Which PDF format downloads and shares use. Remembered across sessions and
  // shared with the editor, so picking one place changes both.
  const [listTheme, setListTheme] = useState(loadThemeChoice);
  const pickTheme = (key) => { setListTheme(key); saveThemeChoice(key); };
  const [filters, setFilters] = useState({
    status: searchParams.get('status') || '',
  });

  const load = () => {
    setLoading(true);
    const params = {};
    if (filters.status) params.status = filters.status;
    api.get('/sales-quotations', { params }).then(r => setList(r.data)).catch(() => toast.error('Failed to load quotations')).finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters.status]);

  useEffect(() => {
    const statusFromUrl = searchParams.get('status') || '';
    setFilters(p => ({ ...p, status: statusFromUrl }));
    // eslint-disable-next-line
  }, [searchParams]);

  const downloadPDF = async (q) => {
    setPdfBusy(q._id);
    try { const doc = await buildQuotationPDF(q, listTheme); doc.save(`${q.quotationNo}.pdf`); }
    catch { toast.error('Could not generate PDF'); }
    finally { setPdfBusy(''); }
  };

  // Shares a quotation via WhatsApp or Email. Where the browser supports the
  // native Web Share API with files (most Android/Chrome and iOS/Safari
  // browsers), this hands the PDF straight to the OS share sheet — the user
  // taps WhatsApp (or Gmail/Mail) and the file is already attached, no manual
  // download-then-attach step needed. Desktop browsers that don't support
  // file sharing fall back to downloading the PDF and opening a pre-filled
  // WhatsApp/email link, same as before.
  const shareQuote = async (q, channel) => {
    setShareMenuFor(null);
    setPdfBusy(q._id);
    let doc = null;
    try {
      doc = await buildQuotationPDF(q, listTheme);
    } catch { toast.error('Could not generate PDF'); }
    finally { setPdfBusy(''); }
    if (!doc) return;

    const message = `Hi ${q.clientName || 'there'}, here is your quotation ${q.quotationNo} for ${q.projectType || 'your project'} — Grand Total: ${inr(q.grandTotal)}. - Wedoo Interiors`;

    // Try the native share sheet first — this is the only way a browser can
    // hand a file straight to WhatsApp/Mail without the user attaching it
    // themselves manually.
    try {
      const blob = doc.output('blob');
      const file = new File([blob], `${q.quotationNo}.pdf`, { type: 'application/pdf' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: `Quotation ${q.quotationNo}`, text: message });
        return;
      }
    } catch (err) {
      if (err && err.name === 'AbortError') return; // user cancelled the share sheet
      // any other error (unsupported, blocked, etc.) — fall through to the link-based fallback below
    }

    // Fallback: this browser can't attach files to a share — download the PDF
    // and open WhatsApp/Email pre-filled, asking the user to attach it.
    doc.save(`${q.quotationNo}.pdf`);
    toast('PDF downloaded — please attach it in the chat/email that just opened.');
    if (channel === 'whatsapp') {
      const digits = (q.clientMobile || '').replace(/[^0-9]/g, '');
      const phone = digits ? (digits.length === 10 ? `91${digits}` : digits) : '';
      const params = new URLSearchParams();
      if (phone) params.set('phone', phone);
      params.set('text', `${message} The PDF has just been downloaded on this device — please attach it here.`);
      window.open(`https://api.whatsapp.com/send?${params.toString()}`, '_blank');
    } else if (channel === 'email') {
      const subject = `Quotation ${q.quotationNo} - Wedoo Interiors`;
      const body = `${message} The PDF has just been downloaded on this device — please attach it here.`;
      window.location.href = `mailto:${q.clientEmail || ''}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    }
  };

  const del = async (q) => {
    if (!window.confirm(`Delete quotation ${q.quotationNo}? This cannot be undone.`)) return;
    try { await api.delete(`/sales-quotations/${q._id}`); toast.success('Deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.5rem' }}>
        <div>
          <p style={{ fontSize: '0.68rem', letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--gold)', marginBottom: '0.4rem' }}>Quotations</p>
          <h1 style={{ fontFamily: 'var(--font-serif)', fontSize: '1.8rem', color: 'var(--cream)' }}>All Quotations</h1>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <label className="qb-theme-pick" title={resolveTheme(listTheme).description}>
            <span>Format</span>
            <select value={listTheme} onChange={e => pickTheme(e.target.value)}>
              {DOC_THEMES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </label>
          <Link to="/admin/quotation/new" className="btn btn-gold" style={{ fontSize: '0.75rem' }}>+ Create Quotation</Link>
        </div>
      </div>

      <div className="card ql-wrap" style={{ overflowX: 'auto' }}>
        {loading ? (
          <p className="admin-empty">Loading...</p>
        ) : list.length === 0 ? (
          <p className="admin-empty">No quotations found. Create your first one above.</p>
        ) : (
          <table className="ql-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem', minWidth: '820px' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--ink-5)' }}>
                {['Quotation No', 'Client Name', 'Project', 'Amount', 'Status', 'Date', 'Action'].map(h => (
                  <th key={h} style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.65rem', color: 'var(--muted)', letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 500 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {list.map(q => (
                <tr key={q._id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                  <td data-label="Quotation No" style={{ padding: '0.8rem 1rem', color: 'var(--gold)', fontWeight: 500 }}>{q.quotationNo}</td>
                  <td data-label="Client" style={{ padding: '0.8rem 1rem', color: 'var(--cream-2)' }}>{q.clientName}</td>
                  <td data-label="Project" style={{ padding: '0.8rem 1rem', color: 'var(--muted)' }}>{q.projectType || '—'}</td>
                  <td data-label="Amount" style={{ padding: '0.8rem 1rem', color: 'var(--cream-2)' }}>{inr(q.grandTotal)}</td>
                  <td data-label="Status" style={{ padding: '0.8rem 1rem' }}><StatusBadge status={q.status} /></td>
                  <td data-label="Date" style={{ padding: '0.8rem 1rem', color: 'var(--muted)' }}>{fmtDate(q.quotationDate)}</td>
                  <td data-label="Action" style={{ padding: '0.8rem 1rem' }}>
                    <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', position: 'relative' }}>
                      <Link to={`/admin/quotation/${q._id}/preview`} style={{ color: 'var(--gold)', fontSize: '0.72rem' }}>View</Link>
                      <Link to={`/admin/quotation/${q._id}`} style={{ color: 'var(--muted)', fontSize: '0.72rem' }}>Edit</Link>
                      <button onClick={() => downloadPDF(q)} disabled={pdfBusy === q._id} style={{ color: 'var(--muted)', fontSize: '0.72rem', background: 'none', cursor: 'pointer' }}>
                        {pdfBusy === q._id ? '...' : 'PDF ⬇'}
                      </button>
                      <button
                        onClick={(e) => {
                          if (shareMenuFor && shareMenuFor.id === q._id) { setShareMenuFor(null); return; }
                          const r = e.currentTarget.getBoundingClientRect();
                          setShareMenuFor({ id: q._id, top: r.bottom + 4, left: r.right - 150 });
                        }}
                        disabled={pdfBusy === q._id}
                        style={{ color: 'var(--muted)', fontSize: '0.72rem', background: 'none', cursor: 'pointer' }}
                      >Share ▾</button>
                      {shareMenuFor && shareMenuFor.id === q._id && createPortal(
                        <>
                          <div onClick={() => setShareMenuFor(null)} style={{ position: 'fixed', inset: 0, zIndex: 249 }} />
                          <div style={{ position: 'fixed', top: shareMenuFor.top, left: shareMenuFor.left, background: '#1c1c1c', border: '1px solid rgba(245,168,0,0.2)', minWidth: '150px', zIndex: 250, boxShadow: '0 8px 24px rgba(0,0,0,0.5)', borderRadius: '4px', overflow: 'hidden' }}>
                            <button onClick={() => shareQuote(q, 'whatsapp')} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '0.55rem 0.8rem', background: 'none', border: 'none', color: 'var(--cream-2)', fontSize: '0.72rem', cursor: 'pointer' }}>📱 WhatsApp</button>
                            <button onClick={() => shareQuote(q, 'email')} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '0.55rem 0.8rem', background: 'none', border: 'none', borderTop: '1px solid rgba(255,255,255,0.06)', color: 'var(--cream-2)', fontSize: '0.72rem', cursor: 'pointer' }}>✉️ Email</button>
                          </div>
                        </>,
                        document.body
                      )}
                      <button onClick={() => del(q)} style={{ color: '#f87171', fontSize: '0.72rem', background: 'none', cursor: 'pointer' }}>✕</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  CREATE / EDIT QUOTATION
// ═══════════════════════════════════════════════════════════════════
export function AdminQuotationForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [docTheme, setDocTheme] = useState(loadThemeChoice);
  // Dropdown options saved by admins, merged on top of CATEGORY_PRESETS.
  const [presets, setPresets] = useState({ items: [], materials: [] });
  const [loaded, setLoaded] = useState(!id);
  const [form, setForm] = useState({
    quotationNo: '', quotationDate: toInputDate(new Date()), validUntil: '',
    clientName: '', clientMobile: '', clientEmail: '', projectAddress: '', projectType: '',
    salesPerson: user?.name || '',
    categories: [emptyCategory('Civil Works')],
    discountType: 'percent', discountValue: 0,
    gstPercent: 18,
    transportationCharges: 0, installationCharges: 0, additionalCharges: 0, additionalChargesNote: '',
    advancePaymentType: 'percent', advancePaymentValue: 50,
    paymentTerms: '50% Advance at the time of order confirmation.\n40% during execution.\nRemaining 10% before project handover.',
    termsConditions: [
      'This quotation is valid for 15 days from the quotation date.',
      'Material prices are subject to market availability.',
      'Any additional work outside the agreed scope will be charged separately.',
      'Project timelines depend on material availability and site conditions.',
      'GST and applicable taxes will be charged as per government regulations.',
      'Changes requested after approval may affect the final project cost and timeline.',
    ].join('\n'),
    notes: '', status: 'Draft',
  });

  useEffect(() => {
    api.get('/quotation-presets')
      .then(r => setPresets({
        items: (r.data || []).filter(p => p.kind === 'item').map(p => ({ name: p.name, category: p.category || '' })),
        materials: (r.data || []).filter(p => p.kind === 'material').map(p => p.name).sort(),
      }))
      .catch(() => {}); // dropdowns still work from the built-in presets
  }, []);

  useEffect(() => {
    if (!id) return;
    api.get(`/sales-quotations/${id}`).then(r => {
      const q = r.data;
      setForm(p => ({
        ...p, ...q,
        quotationDate: toInputDate(q.quotationDate),
        validUntil: toInputDate(q.validUntil),
        categories: q.categories?.length ? q.categories : [emptyCategory()],
      }));
    }).catch(() => toast.error('Failed to load quotation')).finally(() => setLoaded(true));
  }, [id]);

  // Category / item helpers
  const addCategory = () => setForm(p => ({ ...p, categories: [...p.categories, emptyCategory()] }));
  const removeCategory = (ci) => setForm(p => ({ ...p, categories: p.categories.filter((_, i) => i !== ci) }));
  const updateCategoryName = (ci, name) => setForm(p => { const c = [...p.categories]; c[ci] = { ...c[ci], categoryName: name }; return { ...p, categories: c }; });
  const addItem = (ci) => setForm(p => { const c = [...p.categories]; c[ci] = { ...c[ci], items: [...c[ci].items, emptyItem()] }; return { ...p, categories: c }; });
  const removeItem = (ci, ii) => setForm(p => { const c = [...p.categories]; c[ci] = { ...c[ci], items: c[ci].items.filter((_, i) => i !== ii) }; return { ...p, categories: c }; });
  // Adding an option from the dropdown saves it for every future quotation.
  const addPreset = (kind, name, category = '') => {
    setPresets(p => (kind === 'material'
      ? { ...p, materials: [...new Set([...p.materials, name])].sort() }
      : { ...p, items: [...p.items, { name, category }] }));
    api.post('/quotation-presets', { kind, name, category })
      .catch(() => toast.error('Saved to this quotation, but could not add it to the shared list'));
  };

  // Built-in presets for the category plus anything admins have added to it.
  const itemOptions = (categoryName) => [...new Set([
    ...(CATEGORY_PRESETS[categoryName] || []),
    ...presets.items.filter(i => i.category === categoryName).map(i => i.name),
  ])];

  const updateItem = (ci, ii, key, val) => setForm(p => {
    const c = [...p.categories];
    const items = [...c[ci].items];
    items[ii] = { ...items[ii], [key]: val };
    c[ci] = { ...c[ci], items };
    return { ...p, categories: c };
  });

  const save = async (statusOverride) => {
    if (!form.clientName.trim()) { toast.error('Client name is required'); return; }
    if (!isValidOptionalPhone(form.clientMobile)) { toast.error(PHONE_ERROR); return; }
    setSaving(true);
    const payload = { ...form, status: statusOverride || form.status };
    try {
      if (id) {
        const { data } = await api.patch(`/sales-quotations/${id}`, payload);
        toast.success('Quotation updated!');
        setForm(p => ({ ...p, ...data, quotationDate: toInputDate(data.quotationDate), validUntil: toInputDate(data.validUntil) }));
      } else {
        const { data } = await api.post('/sales-quotations', payload);
        toast.success(`Quotation ${data.quotationNo} created!`);
        navigate(`/admin/quotation/${data._id}`);
      }
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const preview = async () => {
    setPdfBusy(true);
    try { const doc = await buildQuotationPDF(form, docTheme); window.open(doc.output('bloburl'), '_blank'); }
    catch { toast.error('Could not build preview'); }
    finally { setPdfBusy(false); }
  };
  const downloadPDF = async () => {
    setPdfBusy(true);
    try { const doc = await buildQuotationPDF(form, docTheme); doc.save(`${form.quotationNo || 'quotation'}.pdf`); }
    catch { toast.error('Could not generate PDF'); }
    finally { setPdfBusy(false); }
  };
  const duplicate = async () => {
    if (!id) return;
    try {
      const clone = { ...form };
      delete clone._id; delete clone.quotationNo; delete clone.createdAt; delete clone.updatedAt; delete clone.__v; delete clone.createdBy;
      clone.status = 'Draft';
      clone.quotationDate = toInputDate(new Date());
      const { data } = await api.post('/sales-quotations', clone);
      toast.success(`Duplicated as ${data.quotationNo}`);
      navigate(`/admin/quotation/${data._id}`);
    } catch { toast.error('Duplicate failed'); }
  };

  if (!loaded) return <p className="admin-empty">Loading...</p>;

  return (
    <div className="qb">
      <div className="qb-head">
        <div>
          <button className="qb-back" onClick={() => navigate('/admin/quotation')}>← Back to Quotations</button>
          <p className="qb-eyebrow">{id ? 'Editing' : 'New Document'}</p>
          <h1 className="qb-title">
            {id ? 'Edit Quotation' : 'New Quotation'} {form.quotationNo && <span style={{ color: 'var(--gold)' }}>· {form.quotationNo}</span>}
          </h1>
        </div>
        {id && (
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
            <label className="qb-theme-pick" title={resolveTheme(docTheme).description}>
              <span>Format</span>
              <select
                value={docTheme}
                onChange={e => { setDocTheme(e.target.value); saveThemeChoice(e.target.value); }}>
                {DOC_THEMES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
            </label>
            <button onClick={preview} disabled={pdfBusy} className="btn btn-dark" style={{ fontSize: '0.7rem' }}>👁 Preview</button>
            <button onClick={downloadPDF} disabled={pdfBusy} className="btn btn-dark" style={{ fontSize: '0.7rem' }}>⬇ Download PDF</button>
            <button onClick={duplicate} className="btn btn-dark" style={{ fontSize: '0.7rem' }}>⧉ Duplicate</button>
          </div>
        )}
      </div>

      <div>
        <div style={{ minWidth: 0 }}>
          {/* Customer Information */}
          <div className="qb-card">
            <div className="qb-card-head"><h3 className="qb-card-title">Customer Information</h3></div>
            <div className="grid-2">
              <FieldGroup label="Quotation Number"><input value={form.quotationNo || '(auto-generated on save)'} disabled style={{ opacity: 0.6 }} /></FieldGroup>
              <FieldGroup label="Quotation Date"><input type="date" value={form.quotationDate} onChange={e => setForm(p => ({ ...p, quotationDate: e.target.value }))} /></FieldGroup>
              <FieldGroup label="Client Name *"><input value={form.clientName} onChange={e => setForm(p => ({ ...p, clientName: e.target.value }))} placeholder="e.g. Raj Kumar" required /></FieldGroup>
              <FieldGroup label="Mobile Number"><input {...phoneInputProps} value={form.clientMobile} onChange={e => setForm(p => ({ ...p, clientMobile: sanitizePhone(e.target.value) }))} placeholder="10-digit mobile number" /></FieldGroup>
              <FieldGroup label="Email Address"><input type="email" value={form.clientEmail} onChange={e => setForm(p => ({ ...p, clientEmail: e.target.value }))} placeholder="client@email.com" /></FieldGroup>
              <FieldGroup label="Valid Until"><input type="date" value={form.validUntil} onChange={e => setForm(p => ({ ...p, validUntil: e.target.value }))} /></FieldGroup>
              <FieldGroup label="Project Type">
                <select value={form.projectType} onChange={e => setForm(p => ({ ...p, projectType: e.target.value }))}>
                  <option value="">Select type...</option>
                  {PROJECT_TYPES.map(pt => <option key={pt} value={pt}>{pt}</option>)}
                </select>
              </FieldGroup>
              <FieldGroup label="Sales Person / Designer"><input value={form.salesPerson} onChange={e => setForm(p => ({ ...p, salesPerson: e.target.value }))} /></FieldGroup>
            </div>
            <FieldGroup label="Project Address"><textarea rows={2} value={form.projectAddress} onChange={e => setForm(p => ({ ...p, projectAddress: e.target.value }))} placeholder="Site / project address" /></FieldGroup>
          </div>

          {/* Categories & Items */}
          <div className="qb-card">
            <div className="qb-card-head">
              <h3 className="qb-card-title">Project Scope &amp; Items</h3>
              <button className="btn btn-gold" style={{ padding: '0.45rem 1rem', fontSize: '0.68rem' }} onClick={addCategory}>+ Add Category</button>
            </div>
            {form.categories.map((cat, ci) => (
              <div key={ci} className="qb-cat">
                <div className="qb-cat-head">
                  <span className="qb-cat-index">{String(ci + 1).padStart(2, '0')}</span>
                  <input className="qb-cat-name" value={cat.categoryName} onChange={e => updateCategoryName(ci, e.target.value)} placeholder="Category name (e.g. Living Room)" />
                  <select
                    className="qb-cat-preset"
                    value={Object.keys(CATEGORY_PRESETS).includes(cat.categoryName) ? cat.categoryName : ''}
                    onChange={e => e.target.value && updateCategoryName(ci, e.target.value)}>
                    <option value="">Preset category...</option>
                    {Object.keys(CATEGORY_PRESETS).map(k => <option key={k} value={k}>{k}</option>)}
                  </select>
                  {form.categories.length > 1 && <button className="qb-cat-remove" onClick={() => removeCategory(ci)}>Remove</button>}
                </div>

                <div className="qb-scroll">
                <table className="qb-table">
                  <thead>
                    <tr>
                      <th rowSpan={2} className="qb-sno-h">#</th>
                      <th rowSpan={2}>Item / Specification</th>
                      <th colSpan={3} className="qb-group">Dimensions (mm)</th>
                      <th rowSpan={2}>Material</th>
                      <th rowSpan={2} className="num">Qty</th>
                      <th rowSpan={2} className="num">Rate (₹)</th>
                      <th rowSpan={2} className="num">Unit/Sft</th>
                      <th rowSpan={2} className="num">Amount (₹)</th>
                      <th rowSpan={2} aria-label="Remove row"></th>
                    </tr>
                    <tr>
                      <th className="num">Length</th>
                      <th className="num">Depth</th>
                      <th className="num">Height</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cat.items.map((item, ii) => {
                      const amount = lineAmount(item);
                      const sft = areaSft(item.length, item.height);
                      const numCell = (key) => (
                        <input type="number" min="0" className="qb-in num qb-dim" value={item[key] ?? ''} placeholder="0"
                          onChange={e => updateItem(ci, ii, key, e.target.value)} />
                      );
                      return (
                        <tr key={ii}>
                          <td className="qb-sno" data-label="#">{ii + 1}</td>
                          <td data-label="Item / Specification" style={{ minWidth: '210px' }}>
                            <SearchableSelect
                              value={item.description}
                              options={itemOptions(cat.categoryName)}
                              onChange={v => updateItem(ci, ii, 'description', v)}
                              onCreate={v => addPreset('item', v, cat.categoryName)}
                              className="qb-in"
                              placeholder="Search or type an item"
                              createLabel="Add item"
                            />
                          </td>
                          <td className="num" data-label="Length (mm)">{numCell('length')}</td>
                          <td className="num" data-label="Depth (mm)">{numCell('depth')}</td>
                          <td className="num" data-label="Height (mm)">{numCell('height')}</td>
                          <td data-label="Material" style={{ minWidth: '210px' }}>
                            <SearchableSelect
                              value={item.material}
                              options={presets.materials}
                              onChange={v => updateItem(ci, ii, 'material', v)}
                              onCreate={v => addPreset('material', v)}
                              className="qb-in"
                              placeholder="Search or type a material"
                              createLabel="Add material"
                            />
                          </td>
                          <td className="num" data-label="Qty">
                            <input type="number" className="qb-in num qb-qty" value={item.quantity} onChange={e => updateItem(ci, ii, 'quantity', e.target.value)} />
                          </td>
                          <td className="num" data-label="Rate (₹)">
                            <input type="number" className="qb-in num qb-rate" value={item.rate} onChange={e => updateItem(ci, ii, 'rate', e.target.value)} />
                          </td>
                          <td className="num" data-label="Unit / Sft">
                            {sft > 0 ? <span className="qb-sft" title="Computed: (length ÷ 300) × (height ÷ 300)">{sft.toFixed(2)}</span> : (
                              <select className="qb-in qb-unit" value={item.unit} onChange={e => updateItem(ci, ii, 'unit', e.target.value)}>
                                {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                              </select>
                            )}
                          </td>
                          <td className="num qb-amount" data-label="Amount (₹)">{inr(amount)}</td>
                          <td className="qb-delcell" style={{ width: '38px', textAlign: 'center' }}>
                            {cat.items.length > 1 && <button className="qb-del" title="Remove item" onClick={() => removeItem(ci, ii)}>✕</button>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>
                <button className="qb-additem" onClick={() => addItem(ci)}>+ Add Item</button>
              </div>
            ))}
          </div>

          {/* Charges */}
          <div className="qb-card">
            <div className="qb-card-head"><h3 className="qb-card-title">Discount, GST &amp; Additional Charges</h3></div>
            <div className="grid-2">
              <FieldGroup label="Discount Type">
                <select value={form.discountType} onChange={e => setForm(p => ({ ...p, discountType: e.target.value }))}>
                  <option value="percent">Percentage (%)</option>
                  <option value="fixed">Fixed (₹)</option>
                </select>
              </FieldGroup>
              <FieldGroup label={`Discount Value ${form.discountType === 'percent' ? '(%)' : '(₹)'}`}><input type="number" value={form.discountValue} onChange={e => setForm(p => ({ ...p, discountValue: e.target.value }))} /></FieldGroup>
              <FieldGroup label="GST (%)"><input type="number" value={form.gstPercent} onChange={e => setForm(p => ({ ...p, gstPercent: e.target.value }))} /></FieldGroup>
              <FieldGroup label="Transportation Charges (₹)"><input type="number" value={form.transportationCharges} onChange={e => setForm(p => ({ ...p, transportationCharges: e.target.value }))} /></FieldGroup>
              <FieldGroup label="Installation Charges (₹)"><input type="number" value={form.installationCharges} onChange={e => setForm(p => ({ ...p, installationCharges: e.target.value }))} /></FieldGroup>
              <FieldGroup label="Additional Charges (₹)"><input type="number" value={form.additionalCharges} onChange={e => setForm(p => ({ ...p, additionalCharges: e.target.value }))} /></FieldGroup>
              <FieldGroup label="Additional Charges Note"><input value={form.additionalChargesNote} onChange={e => setForm(p => ({ ...p, additionalChargesNote: e.target.value }))} placeholder="e.g. Site supervision" /></FieldGroup>
              <FieldGroup label="Advance Payment Type">
                <select value={form.advancePaymentType} onChange={e => setForm(p => ({ ...p, advancePaymentType: e.target.value }))}>
                  <option value="percent">Percentage (%)</option>
                  <option value="fixed">Fixed (₹)</option>
                </select>
              </FieldGroup>
              <FieldGroup label={`Advance Payment ${form.advancePaymentType === 'percent' ? '(%)' : '(₹)'}`}><input type="number" value={form.advancePaymentValue} onChange={e => setForm(p => ({ ...p, advancePaymentValue: e.target.value }))} /></FieldGroup>
            </div>
          </div>

          <div className="qb-card">
            <div className="qb-card-head"><h3 className="qb-card-title">Terms &amp; Notes</h3></div>
            <FieldGroup label="Payment Terms"><textarea rows={4} value={form.paymentTerms} onChange={e => setForm(p => ({ ...p, paymentTerms: e.target.value }))} /></FieldGroup>
            <FieldGroup label="Terms & Conditions"><textarea rows={6} value={form.termsConditions} onChange={e => setForm(p => ({ ...p, termsConditions: e.target.value }))} /></FieldGroup>
            <FieldGroup label="Internal Notes (not shown on PDF)"><textarea rows={2} value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} /></FieldGroup>
          </div>
        </div>

        {/* Status + save — full width, below the form */}
        <div className="qb-card quote-edit-actions">
          <div className="form-group" style={{ marginBottom: 0, flex: '0 0 240px', maxWidth: '240px' }}>
            <label>Status</label>
            <select value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))} style={{ width: '100%' }}>
              {STATUS_LIST.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <button className="btn btn-gold" style={{ minWidth: '220px', justifyContent: 'center' }} onClick={() => save('Draft')} disabled={saving}>
            {saving ? 'Saving...' : '💾 Save Quotation'}
          </button>
        </div>
      </div>
      <style>{`
        /* ── Quotation builder ─────────────────────────────────────────── */
        .qb-head { display:flex; justify-content:space-between; align-items:flex-end; flex-wrap:wrap; gap:1rem;
                   margin-bottom:1.75rem; padding-bottom:1.1rem; border-bottom:1px solid var(--ink-4); }
        .qb-back { color:var(--muted); font-size:0.72rem; letter-spacing:0.06em; background:none; border:none;
                   cursor:pointer; padding:0; margin-bottom:0.7rem; display:block; }
        .qb-back:hover { color:var(--gold); }
        .qb-eyebrow { font-size:0.6rem; letter-spacing:0.22em; text-transform:uppercase; color:var(--gold); margin-bottom:0.4rem; }
        .qb-title { font-family:var(--font-display); font-size:1.75rem; font-weight:600; letter-spacing:-0.02em; color:var(--cream); line-height:1.15; }

        .qb-card { background:#141414; border:1px solid var(--ink-4); padding:1.75rem; margin-bottom:1.25rem; }
        .qb-card-head { display:flex; justify-content:space-between; align-items:center; gap:1rem; flex-wrap:wrap;
                        margin-bottom:1.5rem; padding-bottom:0.85rem; border-bottom:1px solid var(--ink-4); }
        .qb-card-title { font-family:var(--font-serif); font-size:1.15rem; font-weight:400; color:var(--cream);
                         display:flex; align-items:center; gap:0.65rem; }
        .qb-card-title::before { content:''; width:3px; height:15px; background:var(--gold); flex:none; }

        /* category block */
        .qb-cat { border:1px solid var(--ink-4); background:rgba(255,255,255,0.012); margin-bottom:1.1rem; }
        .qb-cat:last-of-type { margin-bottom:0; }
        .qb-cat-head { display:flex; gap:0.75rem; align-items:center; flex-wrap:wrap;
                       padding:0.85rem 1rem; background:rgba(245,168,0,0.045); border-bottom:1px solid var(--ink-4); }
        .qb-cat-index { font-size:0.62rem; letter-spacing:0.12em; color:var(--gold); opacity:0.75; flex:none; }
        .qb-cat-name { flex:1 1 200px; min-width:0; background:transparent; border:none; border-bottom:1px solid var(--gold-dim);
                       color:var(--gold); font-weight:600; font-size:0.88rem; padding:0.35rem 0; border-radius:0; }
        .qb-cat-name:focus { outline:none; border-bottom-color:var(--gold); }
        .qb-cat-preset { flex:0 0 auto; max-width:190px; background:var(--ink-3); border:1px solid var(--ink-5);
                         color:var(--cream); padding:0.42rem 0.55rem; font-size:0.72rem; border-radius:var(--radius); }
        .qb-cat-remove { color:#f87171; background:none; border:1px solid rgba(248,113,113,0.25); cursor:pointer;
                         padding:0.35rem 0.75rem; font-size:0.62rem; letter-spacing:0.12em; text-transform:uppercase;
                         border-radius:var(--radius); }
        .qb-cat-remove:hover { background:rgba(248,113,113,0.1); }

        /* items table */
        .qb-scroll { overflow-x:auto; padding:0.25rem 1rem 0.6rem; }
        .qb-table { width:100%; min-width:1080px; border-collapse:separate; border-spacing:0; font-size:0.78rem; }
        .qb-table th { font-size:0.6rem; font-weight:500; letter-spacing:0.12em; text-transform:uppercase; color:var(--muted);
                       padding:0.7rem 0.45rem; text-align:left; border-bottom:1px solid var(--ink-5); white-space:nowrap; }
        .qb-table th.num, .qb-table td.num { text-align:right; }
        .qb-table th.qb-group { text-align:center; color:var(--gold); border-bottom:1px solid var(--gold-dim); }
        .qb-table th.qb-sno-h { width:34px; text-align:center; }
        .qb-table td { padding:0.45rem; border-bottom:1px solid rgba(255,255,255,0.04); vertical-align:middle; }
        .qb-table tbody tr:last-child td { border-bottom:none; }
        .qb-table tbody tr:hover td { background:rgba(255,255,255,0.022); }
        .qb-sno { width:34px; text-align:center; color:var(--muted); font-size:0.72rem; font-variant-numeric:tabular-nums; }

        .qb-in { width:100%; background:var(--ink-3); border:1px solid var(--ink-5); color:var(--cream);
                 padding:0.48rem 0.6rem; font-size:0.78rem; transition:border-color .15s, background .15s; }
        .qb-in:focus { outline:none; border-color:var(--gold); background:var(--ink-4); }
        .qb-in::placeholder { color:#6b6459; }
        .qb-in.num { text-align:right; font-variant-numeric:tabular-nums; }
        .qb-dim { width:78px; }
        .qb-qty { width:62px; }
        .qb-rate { width:94px; }
        .qb-unit { width:88px; font-size:0.72rem; padding:0.42rem 0.4rem; }
        /* the spinner arrows steal width in narrow numeric cells */
        .qb-in[type=number]::-webkit-outer-spin-button,
        .qb-in[type=number]::-webkit-inner-spin-button { -webkit-appearance:none; margin:0; }
        .qb-in[type=number] { -moz-appearance:textfield; }

        .qb-sft { display:inline-block; min-width:62px; text-align:right; font-variant-numeric:tabular-nums;
                  color:var(--cream-2,#cfc8bd); background:rgba(245,168,0,0.07); border:1px solid rgba(245,168,0,0.18);
                  padding:0.45rem 0.55rem; font-size:0.76rem; border-radius:var(--radius); }
        .qb-amount { color:var(--gold); font-weight:600; font-variant-numeric:tabular-nums; white-space:nowrap; }
        .qb-del { width:26px; height:26px; line-height:1; border-radius:50%; border:1px solid transparent;
                  color:#7a7168; background:none; cursor:pointer; font-size:0.75rem; }
        .qb-del:hover { color:#f87171; border-color:rgba(248,113,113,0.35); background:rgba(248,113,113,0.08); }

        .qb-additem { display:inline-block; width:auto; margin:0 1rem 1rem; padding:0.55rem 1.15rem;
                      border:1px dashed var(--ink-5); background:none; color:var(--gold); cursor:pointer;
                      font-size:0.68rem; letter-spacing:0.14em; text-transform:uppercase; border-radius:var(--radius); }
        .qb-additem:hover { border-color:var(--gold-dim); background:rgba(245,168,0,0.04); }

        .quote-edit-actions { display:flex; align-items:flex-end; gap:1rem; flex-wrap:wrap; margin-bottom:2rem; }
        .quote-edit-actions .btn { margin-left:auto; }

        /* Phones: the 10-column item table cannot shrink, so each row becomes
           a labelled card instead of something you scroll sideways through. */
        @media (max-width: 820px) {
          .qb-scroll { overflow-x:visible; padding:0.75rem; }
          .qb-table { min-width:0; display:block; }
          .qb-table thead { display:none; }
          .qb-table tbody, .qb-table tr, .qb-table td { display:block; width:auto; }
          .qb-table tr { border:1px solid var(--ink-5); border-radius:var(--radius);
                         padding:0.65rem 0.75rem 0.75rem; margin-bottom:0.85rem; position:relative;
                         background:rgba(255,255,255,0.015); }
          .qb-table tbody tr:hover td { background:transparent; }
          .qb-table td { border-bottom:none; padding:0.3rem 0;
                         display:grid; grid-template-columns:8.5rem minmax(0,1fr); align-items:center; gap:0.6rem; }
          .qb-table td::before { content:attr(data-label); font-size:0.6rem; letter-spacing:0.1em;
                                 text-transform:uppercase; color:var(--muted); }
          .qb-table td.num { text-align:left; }
          .qb-table .qb-in, .qb-table .qb-dim, .qb-table .qb-qty,
          .qb-table .qb-rate, .qb-table .qb-unit { width:100%; }
          .qb-table .qb-sft { width:100%; text-align:left; }
          /* the row number rides in the corner, the delete button opposite it */
          .qb-table td.qb-sno { position:absolute; top:-0.65rem; left:0.7rem; width:auto; padding:0 0.4rem;
                                display:inline-block; background:#141414; color:var(--gold); font-size:0.65rem; }
          .qb-table td.qb-sno::before { content:'Item '; }
          .qb-table td.qb-delcell { position:absolute; top:0.35rem; right:0.4rem; width:auto; padding:0; display:block; }
          .qb-table td.qb-delcell::before { content:none; }
          .qb-cat-head { gap:0.5rem; }
          .qb-cat-preset { max-width:none; flex:1 1 140px; }
          .qb-additem { width:calc(100% - 1.5rem); margin:0 0.75rem 0.75rem; }
          .qb-theme-pick { width:100%; justify-content:space-between; }
        }

        @media (max-width: 600px) {
          .qb-card { padding:1.1rem; }
          .qb-title { font-size:1.5rem; }
          .qb-head .btn { flex:1 1 calc(50% - 0.25rem); justify-content:center; }
          .quote-edit-actions { flex-direction:column; align-items:stretch; }
          .quote-edit-actions .form-group { flex:1 1 auto !important; max-width:none !important; }
          .quote-edit-actions .btn { margin-left:0; }
          .quote-edit-actions .btn { width:100%; }
        }
      `}</style>
    </div>
  );
}
